import {
  eventId,
  initialHlc,
  logicalDate,
  parseHlc,
  serializeHlc,
  tickHlc,
  type Hlc,
} from '@selflevel/domain';
import { db, type Settings } from './db';

/**
 * LA SESIÓN LOCAL: quién es este dispositivo y qué hora lógica es.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * DECISIÓN DE PRODUCTO: **la app funciona entera sin cuenta.**
 *
 * Se abre y se usa. Hábitos, foco, diario, progresión, rewind: todo local, todo
 * al instante. La cuenta es opcional y solo sirve para una cosa —sincronizar
 * entre dispositivos y tener copia—, y se ofrece cuando el usuario ya tiene algo
 * que perder, no en la primera pantalla.
 *
 * Pedir correo y contraseña antes de dejar marcar una casilla es la forma más
 * eficaz de que nadie marque la casilla. Y para una app de hábitos, el día uno
 * es el único día que importa.
 *
 * Por eso hay un `userId` local desde el principio: los identificadores
 * deterministas lo necesitan, y el día que se cree la cuenta las filas ya
 * existentes se reetiquetan sin perder nada.
 * ───────────────────────────────────────────────────────────────────────────
 */

const KEY_DEVICE = 'deviceId';
const KEY_USER = 'localUserId';
const KEY_HLC = 'hlc';

let deviceId: string | null = null;
let userId: string | null = null;
let clock: Hlc | null = null;

async function readMeta<T>(key: string): Promise<T | undefined> {
  const row = await db.meta.get(key);
  return row?.value as T | undefined;
}

export async function initSession(): Promise<{ deviceId: string; userId: string }> {
  let device = await readMeta<string>(KEY_DEVICE);
  if (!device) {
    device = eventId();
    await db.meta.put({ key: KEY_DEVICE, value: device });
  }

  let user = await readMeta<string>(KEY_USER);
  if (!user) {
    user = eventId();
    await db.meta.put({ key: KEY_USER, value: user });
  }

  const stored = await readMeta<string>(KEY_HLC);
  clock = stored ? parseHlc(stored) : initialHlc(device);

  deviceId = device;
  userId = user;
  return { deviceId: device, userId: user };
}

export function getDeviceId(): string {
  if (!deviceId) throw new Error('la sesión no está iniciada');
  return deviceId;
}

export function getUserId(): string {
  if (!userId) throw new Error('la sesión no está iniciada');
  return userId;
}

/**
 * Al vincular, los ids deterministas pasan a calcularse con el id de la cuenta.
 * Con el id local —uno aleatorio por dispositivo— el móvil y el portátil daban
 * ids distintos al mismo hábito del mismo día, y el servidor rechazaba la
 * segunda escritura por su clave natural.
 */
export async function adoptarUsuario(cuentaId: string): Promise<void> {
  await db.meta.put({ key: KEY_USER, value: cuentaId });
  userId = cuentaId;
}

/**
 * Emite el siguiente HLC. Se persiste en cada emisión: si la pestaña se cierra a
 * mitad, el reloj no retrocede al volver — y un reloj que retrocede es una
 * escritura que pierde contra su propio pasado.
 */
export async function nextHlc(): Promise<string> {
  if (!clock) throw new Error('la sesión no está iniciada');
  clock = tickHlc(clock, Date.now());
  const serialized = serializeHlc(clock);
  await db.meta.put({ key: KEY_HLC, value: serialized });
  return serialized;
}

export const DEFAULT_SETTINGS: Settings = {
  id: 'unica',
  displayName: '',
  dayStartOffsetMin: 240,
  tz: 'UTC',
  locale: 'es',
  theme: 'dark',
  skin: 'expresiva',
  transparency: 'standard',
  focusDefaultMinutes: 25,
  focusBreakMinutes: 5,
  focusLongBreakMinutes: 15,
  focusCyclesBeforeLong: 4,
  birthDate: null,
  journalPrivateMode: false,
  onboardedAt: null,
  levelFloor: 0,
  lastSeenSeason: null,
  lastSeenMilestone: null,
};

export async function getSettings(): Promise<Settings> {
  const stored = await db.settings.get('unica');
  // Lo guardado antes de que existiera un ajuste no lo trae: va el de partida.
  if (stored) return { ...DEFAULT_SETTINGS, ...stored };

  const fresh: Settings = {
    ...DEFAULT_SETTINGS,
    tz: zonaDelDispositivo(),
  };
  await db.settings.put(fresh);
  return fresh;
}

/**
 * La zona IANA en la que está el dispositivo AHORA, no la del alta.
 *
 * `settings.tz` se fijaba al hacer el alta y no cambiaba nunca: de viaje, «hoy» se
 * calculaba con el huso de casa y cada casilla se sellaba con él, así que el perdón
 * del viaje (PRD 7.4) no podía ver ningún cambio de zona. La zona de verdad es la
 * del dispositivo; `settings.tz` se mantiene igual a ella (ver `reloj.ts`) porque
 * es lo que usa el servidor para los avisos. `respaldo` solo se usa si el
 * navegador no sabe decirla.
 */
export function zonaDelDispositivo(respaldo = 'UTC'): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || respaldo;
  } catch {
    return respaldo;
  }
}

/**
 * Hoy, según el usuario. No según UTC, y no según el reloj de pared: según la
 * zona en la que está su dispositivo y su «mi día empieza a las…».
 */
export function today(settings: Pick<Settings, 'tz' | 'dayStartOffsetMin'>): string {
  return logicalDate(new Date(), zonaDelDispositivo(settings.tz), settings.dayStartOffsetMin);
}

/**
 * Si el reloj de este dispositivo es plausible. Lo comprueba también el
 * servidor —es el único que puede—, pero marcarlo ya en el cliente evita que un
 * móvil con la hora mal puesta infle su propia progresión local mientras está
 * sin conexión.
 *
 * El margen es generoso a propósito: esto no es seguridad, es higiene.
 */
export function isClockPlausible(): boolean {
  const year = new Date().getUTCFullYear();
  return year >= 2024 && year <= 2100;
}

/**
 * El reloj local aprende de un HLC remoto.
 *
 * Es lo que hace que dos dispositivos que nunca se han visto acaben con relojes
 * comparables: cada uno adelanta el suyo al ver uno mayor, así que el orden lógico
 * converge sin que hablen entre ellos ni con un servidor de tiempo.
 *
 * Se persiste en la misma operación, porque un reloj que retrocede al recargar la
 * pestaña es una escritura que pierde contra su propio pasado.
 */
export async function guardarHlcRemoto(mezclar: (local: Hlc) => string): Promise<void> {
  if (!clock) return;
  const serializado = mezclar(clock);
  clock = parseHlc(serializado);
  await db.meta.put({ key: KEY_HLC, value: serializado });
}
